<template>
  <div
    class="modal"
    @keydown.shift.tab.exact="trapTabKey"
    @keydown.tab.exact="trapTabKey"
    @keydown.esc.exact="closeModal"
  >
    <div class="modalBackdrop" @click.prevent="closeModal" />
    <div role="dialog" aria-modal="true" class="modalDialog">
      <div class="modalContent">
        <!-- HEADER -->
        <header class="modalHeader">
          <button
            v-if="!blockClose.includes(modal.modalType)"
            tabindex="0"
            class="btn btn-small btn-modal-close modalCloseArea"
            @click.prevent="closeModal"
          >
            <span>{{ t('interface.close') }}</span>
            <svg
              class="ico-error"
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 512 512"
            >
              <use href="#ico-error" xlink:href="#ico-error" />
            </svg>
          </button>
        </header>

        <!-- BODY -->
        <!-- unauthorized -->
        <div v-if="modal.modalType === 'unauthorized'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 512 512"
          >
            <use fill="#73cfff" href="#ico-info-xl" xlink:href="#ico-info-xl" />
          </svg>
          <p class="first">
            {{ t('pages.changePassword.resultScreens.unauthorized') }}
          </p>
        </div>

        <!-- offline -->
        <div v-if="modal.modalType === 'offline'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <h5 v-html="t('interface.offline.title')" />
          <p v-html="t('interface.offline.body')" />
        </div>

        <!-- invalidation -->
        <div v-if="modal.modalType === 'invalidate'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <h5 v-html="t('interface.invalidationModal.title')" />
          <p v-html="t('interface.invalidationModal.body')" />
          <p>{{ Helpers.secondsToMinutes(remainingTime) }}</p>
        </div>

        <!-- change pass success -->
        <div v-if="modal.modalType === 'success'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <h5>
            {{ t('pages.changePassword.resultScreens.successTitle') }}
          </h5>
          <div
            class="modalBodyText"
            v-html="t('pages.changePassword.resultScreens.successBody')"
          />
        </div>

        <!-- sporopay key success -->
        <div v-if="modal.modalType === 'sporopayKeySuccess'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <h5>{{ t('pages.sporopayKey.modal.successText') }}</h5>
        </div>

        <!-- setAliasSuccess -->
        <div v-if="modal.modalType === 'setAliasSuccess'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <p class="first" v-html="aliasSuccessText" />
        </div>

        <!-- authBlockSuccess -->
        <div v-if="modal.modalType === 'authBlockSuccess'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <p class="first">
            {{ t('pages.authTools.st.authToolBlockModalSuccess') }}
          </p>
        </div>

        <!-- authBlockFailed -->
        <div v-if="modal.modalType === 'authBlockFailed'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <p class="first">
            {{ t('pages.authTools.st.authToolBlockModalFailed') }}
          </p>
        </div>

        <!-- epinSuccess-{ACTIVE/ACTIVATION/BLOCKED} -->
        <div v-if="modal.modalType.includes('epinSuccess')" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <template v-if="modal.modalType.includes('ACTIVATION')">
            <h5>
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.successTitleActivated',
                )
              }}
            </h5>
            <p class="first">
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.successBodyActivated',
                )
              }}
            </p>
          </template>
          <template v-else>
            <h5>
              {{
                t('pages.authTools.epin.edit.resultScreens.successTitleChanged')
              }}
            </h5>
            <p class="first">
              {{
                t('pages.authTools.epin.edit.resultScreens.successBodyChanged')
              }}
            </p>
          </template>
        </div>

        <!-- epinFailed-{ACTIVE/ACTIVATION/BLOCKED/BLOCKED_FRAUD/AUTH_LIMIT_EXCEEDED} -->
        <div v-if="modal.modalType.includes('epinFailed')" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <template v-if="modal.modalType.includes('BLOCKED_FRAUD')">
            <h5>
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.failedTitleBlockedFraud',
                )
              }}
            </h5>
            <p class="first">
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.failedBodyBlockedFraud',
                )
              }}
            </p>
          </template>
          <template v-else-if="modal.modalType.includes('AUTH_LIMIT_EXCEEDED')">
            <h5>
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.failedTitleNoAuthExceed',
                )
              }}
            </h5>
            <p class="first">
              {{
                t(
                  'pages.authTools.epin.edit.resultScreens.failedBodyNoAuthExceed',
                )
              }}
            </p>
          </template>
          <template v-else>
            <h5
              v-text="
                t(
                  `pages.authTools.epin.edit.resultScreens.${
                    modal.modalType.includes('ACTIVATION')
                      ? 'failedTitleActivated'
                      : 'failedTitleChanged'
                  }`,
                )
              "
            />
          </template>
        </div>

        <!-- AlreadyProcessingWarning -->
        <div
          v-if="modal.modalType === 'AlreadyProcessingWarning'"
          class="modalBody"
        >
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <h5>
            {{
              t(
                'pages.authTools.mToken.addNewProcess.step3.alreadyInProcessModal.title',
              )
            }}
          </h5>
          <p
            v-html="
              t(
                'pages.authTools.mToken.addNewProcess.step3.alreadyInProcessModal.bodyText',
              )
            "
          />
        </div>

        <!-- deleteConsentFailed -->
        <div v-if="modal.modalType === 'deleteConsentFailed'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <p class="first">
            {{ t('pages.tpp.remove.failedModal.bodyText') }}
          </p>
        </div>

        <!-- RequiredSmsWarning -->
        <div v-if="modal.modalType === 'RequiredSmsWarning'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <h5>
            {{
              t(
                'pages.authTools.mToken.addNewProcess.step3.smsKeyNeededModal.title',
              )
            }}
          </h5>
          <p
            v-html="
              t(
                'pages.authTools.mToken.addNewProcess.step3.smsKeyNeededModal.bodyText',
              )
            "
          />
        </div>

        <!-- confirm sporopay key change -->
        <div v-if="modal.modalType === 'sporopayKeyConfirm'" class="modalBody">
          <h5>{{ t('pages.sporopayKey.modal.changeKeyTitle') }}</h5>
          <p>
            {{ t('pages.sporopayKey.modal.changeKeyTextAccountInfo') }}
            <br />
            <span
              class="sporopayKeyAccount"
              v-html="Helpers.formatIban(sporopayKeyData.sporopayKeyAccount)"
            />
            <br />
            <br />
            {{ t('pages.sporopayKey.modal.changeKeyTextCopyInfo') }}
          </p>
          <p class="sporopayKeyClipboard">
            <span>{{ sporopayKeyData.sporopayKey }}</span>
            <button
              v-clipboard="sporopayKeyData.sporopayKey"
              class="btn btn-secondary btn-small"
            >
              {{ t('pages.sporopayKey.modal.changeKeyCopyKeyBtn') }}
            </button>
          </p>
        </div>

        <!-- sporopay key error scope ID -->
        <div
          v-if="modal.modalType === 'sporopayKeyErrorScopeId'"
          class="modalBody"
        >
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <p class="first" v-html="t('pages.sporopayKey.modal.scopeId')" />
        </div>

        <!-- sporopay key error scope merchant -->
        <div
          v-if="modal.modalType === 'sporopayKeyErrorScopeMerchant'"
          class="modalBody"
        >
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <p
            class="first"
            v-html="t('pages.sporopayKey.modal.scopeMerchant')"
          />
        </div>

        <!-- VerifyUrl - 2001 -->
        <div v-if="modal.modalType === 'VerifyUrl_2001'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#00ab56" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <h5>{{ t('pages.smsVerification.modals.2001.title') }}</h5>
          <p v-html="t('pages.smsVerification.modals.2001.body')" />
          <a rel="noopener" target="_blank" :href="shortenerData.shortUrl">
            {{ shortenerData.shortUrl }}
          </a>
        </div>
        <!-- VerifyUrl - 2002 -->
        <div v-if="modal.modalType === 'VerifyUrl_2002'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use fill="#d7d7d7" xlink:href="#icon_success_green_web_mobile-a" />
          </svg>
          <h5>{{ t('pages.smsVerification.modals.2002.title') }}</h5>
          <p v-html="t('pages.smsVerification.modals.2002.body')" />
        </div>
        <!-- VerifyUrl - 4001 -->
        <div v-if="modal.modalType === 'VerifyUrl_4001'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 512 512"
          >
            <use fill="#73cfff" href="#ico-info-xl" xlink:href="#ico-info-xl" />
          </svg>
          <h5>{{ t('pages.smsVerification.modals.4001.title') }}</h5>
          <p
            v-html="
              t('pages.smsVerification.modals.4001.body', { ...shortenerData })
            "
          />
        </div>
        <!-- VerifyUrl - 4002 -->
        <div v-if="modal.modalType === 'VerifyUrl_4002'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 512 512"
          >
            <use fill="#73cfff" href="#ico-info-xl" xlink:href="#ico-info-xl" />
          </svg>
          <h5>{{ t('pages.smsVerification.modals.4002.title') }}</h5>
          <p
            v-html="
              t('pages.smsVerification.modals.4002.body', {
                shortUrl: shortenerData.shortUrl,
              })
            "
          />
        </div>

        <!-- tech error -->
        <div v-if="modal.modalType === 'techError'" class="modalBody">
          <svg
            class="large"
            aria-hidden="true"
            focusable="false"
            width="88"
            height="88"
            viewBox="0 0 88 88"
          >
            <use
              fill="#f94242"
              href="#icon_error_red_web_mobile-a"
              xlink:href="#icon_error_red_web_mobile-a"
            />
          </svg>
          <p class="first">{{ t('interface.techError') }}</p>
        </div>

        <!-- FOOTER -->
        <footer class="modalFooter">
          <button
            v-if="modal.modalType === 'success'"
            class="btn btn-medium btn-primary btn-href"
            @click="redirectAfterDelay(changePassSuccessLoginLink.backLinkUrl)"
          >
            {{ changePassSuccessLoginLink.backLinkText }}
          </button>

          <button
            v-if="modal.modalType === 'setAliasSuccess'"
            class="btn btn-large btn-secondary"
            @click.prevent="resetAlias"
          >
            {{ t('interface.continue') }}
          </button>

          <button
            v-if="
              ['MTokenProcessingOk', 'MTokenProcessingFailed'].includes(
                modal.modalType,
              )
            "
            class="btn btn-large btn-secondary"
            @click.prevent="resetStsInStoreAndRedirectToStList"
          >
            <span v-if="modal.modalType === 'MTokenProcessingFailed'">
              {{ t('interface.finish') }}
            </span>
            <span v-else>{{ t('interface.continue') }}</span>
          </button>

          <button
            v-if="
              ['RequiredSmsWarning', 'AlreadyProcessingWarning'].includes(
                modal.modalType,
              )
            "
            class="btn btn-large btn-secondary"
            @click.prevent="redirectToStList"
          >
            {{ t('interface.continue') }}
          </button>

          <button
            v-if="
              [
                'authBlockFailed',
                'deleteConsentFailed',
                'VerifyUrl_2002',
                'VerifyUrl_4001',
                'VerifyUrl_4002',
              ].includes(modal.modalType)
            "
            class="btn btn-large btn-secondary"
            @click.prevent="closeModal"
          >
            {{ t('interface.close') }}
          </button>

          <button
            v-if="modal.modalType === 'sporopayKeyConfirm'"
            class="btn btn-large btn-primary"
            @click.prevent="confirmSporopayKeyChange"
          >
            {{ t('interface.change') }}
          </button>

          <button
            v-if="
              ['unauthorized', 'authBlockSuccess'].includes(modal.modalType)
            "
            class="btn btn-large btn-secondary"
            @click.prevent="login"
          >
            {{ t('interface.btnLogin') }}
          </button>
          <button
            v-if="
              ['unauthorized', 'authBlockSuccess'].includes(modal.modalType) &&
              [
                'gyfr21xk31nbc7ou',
                'gpboclmi17hzsekh',
                'gpgpojwpp6kifu9h',
                'gpdg91ictbpmqfou',
              ].includes(stateReferer)
            "
            class="btn btn-large btn-primary"
            @click.prevent="toGeorge"
          >
            <span>{{ t('interface.btnBackToGeorge') }}</span>
          </button>
          <button
            v-if="
              ['unauthorized', 'authBlockSuccess'].includes(modal.modalType) &&
              stateReferer === 'bn63pzfvu3mf8ryp'
            "
            class="btn btn-large btn-primary"
            @click.prevent="toB24"
          >
            <span>{{ t('interface.btnBackToB24') }}</span>
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Helpers from '@/classes/Helpers';
import { app } from '@/main.js';
import router from '@/router';
import alias from '@/store/alias';
import global from '@/store/global';
import modal from '@/store/modal';
import smsverification from '@/store/smsverification';
import sporopaykey from '@/store/sporopaykey';
import st from '@/store/st';
import { BackLinkT } from '@/support/types';

const { t } = useI18n();

const blockClose: string[] = [
  'techError',
  'unauthorized',
  'invalidation',
  'offline',
  'MTokenProcessingOk',
  'MTokenProcessingFailed',
  'RequiredSmsWarning',
  'AlreadyProcessingWarning',
  'authBlockSuccess',
  'setAliasSuccess',
  'success',
];

const mountTime = ref(0);

// GETTERS
const shortenerData = computed(() => {
  return {
    shortUrl: `${import.meta.env.VITE_APP_SLSPLINK}${smsverification.hash}`,
    phoneNumber: smsverification.phoneNumber,
  };
});

const sporopayKeyData = computed(() => {
  return sporopaykey.sporopayKeyConfirmModal;
});

const remainingTime = computed(() => {
  return global.trackingClientRemainingTime;
});

const aliasSuccessText = computed(() => {
  let bodyText = '';
  if (alias.aliasOld === '') {
    console.log(alias.aliasNew);
    bodyText = t('pages.alias.aliasSuccessTextNew', {
      newAlias: alias.aliasNew,
    });
  } else {
    bodyText = t('pages.alias.aliasSuccessText', {
      newAlias: alias.aliasNew,
      oldAlias: alias.aliasOld,
    });
  }
  return bodyText;
});

if (modal.modalType === 'offline') {
  global.loaderOpened = false;
}

const setGeorgeProfileBackLink = (
  type: 'blue' | 'green' | 'old' | 'default',
) => {
  const output = {
    isBacklink: true,
    backLinkText: t('interface.btnLoginToGeorge').toString(),
    backLinkUrl: import.meta.env.VITE_APP_GEORGE_PROFILE_URL as string,
  };

  if (type === 'green' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_GREEN) {
    output.backLinkUrl = import.meta.env
      .VITE_APP_GEORGE_PROFILE_URL_GREEN as string;
  }

  if (type === 'blue' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_BLUE) {
    output.backLinkUrl = import.meta.env
      .VITE_APP_GEORGE_PROFILE_URL_BLUE as string;
  }

  if (type === 'old' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_OLD) {
    output.backLinkUrl = import.meta.env
      .VITE_APP_GEORGE_PROFILE_URL_OLD as string;
  }

  return output;
};

const changePassSuccessLoginLink = computed(() => {
  let backlink: BackLinkT = {
    isBacklink: true,
    backLinkText: t('interface.btnLogin').toString(),
    backLinkUrl: window.location.origin,
  };

  switch (global.referer) {
    case 'gbdb5yhx3oljh7gd': // george business
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLoginToGeorge').toString();
      backlink.backLinkUrl = import.meta.env
        .VITE_APP_GEORGE_BUSINESS_URL as string;
      break;
    case 'gbgbhj6uozx35jty': // george business green
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLoginToGeorge').toString();
      backlink.backLinkUrl = import.meta.env
        .VITE_APP_GEORGE_BUSINESS_URL_GREEN as string;
      break;
    case 'gbblua4h9iow27jn': // george business blue
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLoginToGeorge').toString();
      backlink.backLinkUrl = import.meta.env
        .VITE_APP_GEORGE_BUSINESS_URL_BLUE as string;
      break;
    case 'ghfkqedbnneqsjvt': // george
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLoginToGeorge').toString();
      backlink.backLinkUrl = import.meta.env.VITE_APP_GEORGE_URL as string;
      break;
    case 'gyfr21xk31nbc7ou': // george profile
      backlink = setGeorgeProfileBackLink('old');
      break;
    case 'gpboclmi17hzsekh': // george profile
      backlink = setGeorgeProfileBackLink('blue');
      break;
    case 'gpgpojwpp6kifu9h': // george profile
      backlink = setGeorgeProfileBackLink('green');
      break;
    case 'gpdg91ictbpmqfou': // george profile
      backlink = setGeorgeProfileBackLink('default');
      break;
    case 'bn63pzfvu3mf8ryp': // b24
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLoginToB24').toString();
      backlink.backLinkUrl = import.meta.env.VITE_APP_B24_URL as string;
      break;
    default:
      backlink.isBacklink = true;
      backlink.backLinkText = t('interface.btnLogin').toString();
      backlink.backLinkUrl = window.location.origin;
      break;
  }

  return backlink;
});

const closeModal = () => {
  // @TODO: redo into computed variable
  if (blockClose.includes(modal.modalType)) {
    return undefined;
  }
  modal.modalOpened = false;
  const pageFocusedElement = (document.activeElement ||
    document.body) as HTMLElement;
  pageFocusedElement.focus();
  return undefined;
};

// METHODS
/* eslint class-methods-use-this: "off" */
// @TODO: adjust this since we have guards
const login = () => {
  const sourceCookie = global.referer;

  const csrf = Helpers.generateUUID();
  app.$cookies.set('_csrf', csrf, '10MIN');

  // create state param
  const stateParam = `${sourceCookie}||${csrf}`;
  // redirect url for secv
  // const pathName = (window.location.pathname.slice(-1) === '/') ? window.location.pathname.slice(0, -1) : window.location.pathname;
  const pathName =
    router.currentRoute.value.meta?.secvReturn || Helpers.getPathName();
  let redirectUrl = window.location.origin + pathName;
  if (import.meta.env.NODE_ENV === 'development') {
    redirectUrl = `https://slsp.localhost:1234${pathName}`;
  }
  const redirectUrlParam = `&redirect_uri=${encodeURIComponent(redirectUrl)}`;

  // get secv link a make redirect, or throw error
  const serverUrl =
    import.meta.env.NODE_ENV === 'development'
      ? 'http://localhost:6789/api/login'
      : '/api/login';
  axios({
    method: 'GET',
    url: serverUrl,
  })
    .then((response) => {
      // console.log(response.data.loginUrl + stateParam + redirectUrlParam);
      window.location.href =
        response.data.loginUrl + stateParam + redirectUrlParam;
    })
    .catch(() => {
      modal.modalType = 'techError';
      modal.modalOpened = true;
      global.loaderOpened = false;
    });
};

const toGeorge = () => {
  window.location.href = import.meta.env.VITE_APP_GEORGE_URL as string;
};

const toB24 = () => {
  window.location.href = import.meta.env.VITE_APP_B24_URL as string;
};

const redirectAfterDelay = (url: string) => {
  const date = new Date();
  const minWaitingTimeInMs = 3000;
  const diff = date.getTime() - mountTime.value;
  const delay = diff < minWaitingTimeInMs ? minWaitingTimeInMs - diff : 0;

  if (delay) {
    global.loaderOpened = true;
  }

  setTimeout(() => {
    window.location.href = url;
  }, delay);
};

const confirmSporopayKeyChange = () => {
  sporopaykey.sporopayKeyConfirmModal.confirmed = true;
  sporopaykey.sporopayKeyConfirmModal.sporopayKey =
    sporopayKeyData.value.sporopayKey;
  sporopaykey.sporopayKeyConfirmModal.sporopayKeyAccount =
    sporopayKeyData.value.sporopayKeyAccount;
  modal.modalOpened = false;
};

const resetStsInStoreAndRedirectToStList = (): void => {
  st.sts = {};

  nextTick(() => {
    modal.modalOpened = false;
    router.push({ name: 'authTools' });
  });
};

const redirectToStList = (): void => {
  modal.modalOpened = false;
  router.push({ name: 'authTools' });
};

const resetAlias = (): void => {
  modal.modalOpened = false;
  alias.aliasChecked = false;
  router.push({ name: 'dashboard' });
};

onMounted(() => {
  const date = new Date();
  mountTime.value = date.getTime();
});
</script>

<style lang="scss">
@use '@/assets/scss/variables.scss';
@use '@/assets/scss/colors.scss';
@use '@/assets/scss/mixins.scss';

.modal {
  z-index: 1004;
  position: fixed;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  .modalBackdrop {
    z-index: 1600;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;

    background-color: rgba(colors.$g-primary-900, 0.3);
  }

  .modalDialog {
    z-index: 1700;
    background-color: colors.$g-white;
    border-radius: variables.$border-radius;
    box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.2);

    width: 100%;
    @include mixins.breakpoint('sm') {
      width: mixins.calculateRem(550px);
    }
    @include mixins.breakpoint('md') {
      width: mixins.calculateRem(730px);
    }
    @include mixins.breakpoint('lg') {
      width: mixins.calculateRem(888px);
    }

    .modalContent {
      position: relative;

      header.modalHeader {
        min-height: mixins.calculateRem(50px);

        display: flex;
        align-items: center;
      }

      .modalBody {
        padding: mixins.calculateRem(30px) mixins.calculateRem(140px);

        @include mixins.max-breakpoint('md') {
          padding: mixins.calculateRem(30px) mixins.calculateRem(30px);
        }

        max-height: 80vh;
        width: 100%;
        overflow-y: auto;

        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;

        color: colors.$g-primary-900;

        h5 {
          @include mixins.fontSize(24px);
          @include mixins.lineHeight(28px);
          text-align: center;
          padding: mixins.calculateRem(50px) 0 mixins.calculateRem(10px) 0;

          &.first {
            padding-top: mixins.calculateRem(20px);
          }
        }

        p,
        div.modalBodyText {
          width: 100%;
          text-align: center;

          &.first {
            padding-top: mixins.calculateRem(variables.$spacingHeaderContent);
          }

          ul {
            text-align: left;

            li {
              text-align: left;
              list-style-type: disc;
            }
          }

          &.sporopayKeyClipboard {
            // margin-top: mixins.calculateRem(variables.$spacingHeaderContent/4);

            display: flex;
            align-items: center;
            justify-content: center;

            button {
              margin-left: mixins.calculateRem(
                variables.$spacingHeaderContent * 0.5
              );
            }

            @include mixins.max-breakpoint('sm') {
              flex-direction: column;
              button {
                margin-left: 0;
              }
            }
          }
        }

        svg {
          &.large {
            $svgSize: mixins.calculateRem(128px);

            font-size: $svgSize;
            line-height: $svgSize;
            width: $svgSize;
            height: $svgSize;
          }

          &.blue {
            path {
              fill: colors.$eg-ocean;
            }
          }
        }
      }

      .modalFooter {
        // min-height: mixins.calculateRem(106px);

        display: flex;
        justify-content: center;
        align-items: center;

        padding: mixins.calculateRem(variables.$spacingHeaderContent * 0.5);

        button {
          &:not(:last-child) {
            margin-right: mixins.calculateRem(
              variables.$spacingHeaderContent * 0.5
            );
          }
        }
      }
    }
  }
}
</style>
